import { useControls } from 'leva';
import { CylinderGeometry, Group, Mesh, MeshBasicMaterial, Raycaster, SphereGeometry, Vector3 } from 'three';
import { MeshBVHHelper } from 'three-mesh-bvh';

import { Bvh, OrbitControls, useHelper } from '@react-three/drei';
import { Canvas, MeshProps } from '@react-three/fiber';
import { GroupProps, useFrame } from '@react-three/fiber';
import * as React from 'react';

const pointDist = 25;
const raycaster = new Raycaster();
const origVec = new Vector3();
const dirVec = new Vector3();
const cyl = new CylinderGeometry(0.02, 0.02);
const sph = new SphereGeometry(0.25, 20, 20);
const basOrig = new MeshBasicMaterial({ color: 'yellow' });
const basHit = new MeshBasicMaterial({ color: 'red' });
const tra = new MeshBasicMaterial({
  transparent: true,
  opacity: 0.25,
});

function Ray(props: { target: React.MutableRefObject<Group> }) {
  const { target } = props;
  const objRef = React.useRef<Group>(null!);
  const origMesh = React.useRef<Mesh>(null!);
  const hitMesh = React.useRef<Mesh>(null!);
  const cylinderMesh = React.useRef<Mesh>(null!);

  React.useEffect(() => {
    hitMesh.current.scale.multiplyScalar(0.5);
    origMesh.current.position.set(pointDist, 0, 0);
    objRef.current.rotation.x = Math.random() * 10;
    objRef.current.rotation.y = Math.random() * 10;
  }, []);

  const xDir = Math.random() - 0.5;
  const yDir = Math.random() - 0.5;

  useFrame((_, delta) => {
    const obj = objRef.current;
    obj.rotation.x += xDir * delta;
    obj.rotation.y += yDir * delta;
    origMesh.current.updateMatrixWorld();
    origVec.setFromMatrixPosition(origMesh.current.matrixWorld);
    dirVec.copy(origVec).multiplyScalar(-1).normalize();
    raycaster.set(origVec, dirVec);
    raycaster.firstHitOnly = true;
    const res = raycaster.intersectObject(target.current, true);
    const length = res.length ? res[0].distance : pointDist;
    hitMesh.current.position.set(pointDist - length, 0, 0);
    cylinderMesh.current.position.set(pointDist - length / 2, 0, 0);
    cylinderMesh.current.scale.set(1, length, 1);
    cylinderMesh.current.rotation.z = Math.PI / 2;
  });

  return (
    <group ref={objRef}>
      <mesh ref={origMesh} geometry={sph} material={basOrig} />
      <mesh ref={hitMesh} geometry={sph} material={basHit} />
      <mesh ref={cylinderMesh} geometry={cyl} material={tra} />
    </group>
  );
}

function Rays(props: GroupProps) {
  const ref = React.useRef<Group>(null!);
  const { count } = useControls({ count: { value: 100, min: 0, max: 500 } });
  return (
    <>
      <group ref={ref} {...props} />
      {Array.from({ length: count }, (_, id) => (
        <Ray key={id} target={ref} />
      ))}
    </>
  );
}

function Torus(props: MeshProps) {
  const mesh = React.useRef<Mesh>(null!);
  const sphere = React.useRef<Mesh>(null!);
  useHelper(mesh, MeshBVHHelper);
  useFrame((state, delta) => (mesh.current.rotation.x = mesh.current.rotation.y += delta));
  return (
    <mesh
      ref={mesh}
      {...props}
      onPointerMove={(e) => sphere.current.position.copy(mesh.current.worldToLocal(e.point))}
      onPointerOver={() => (sphere.current.visible = true)}
      onPointerOut={() => (sphere.current.visible = false)}
    >
      <torusKnotGeometry args={[1, 0.4, 200, 50]} />
      <meshNormalMaterial />
      <mesh raycast={() => null} ref={sphere} visible={false}>
        <sphereGeometry args={[0.2]} />
        <meshBasicMaterial color="orange" toneMapped={false} />
      </mesh>
    </mesh>
  );
}

export type Props = {};

function App(props: Props) {
  const {} = props;
  const { enabled } = useControls({ enabled: true });
  return (
    <>
      <Canvas
        camera-position-z={40}
        camera-far={100}
        // camera={{ position: [0, 0, 10] }}
        style={{ height: '30vh', marginBottom: '1rem' }}
      >
        <color attach="background" args={['#202025']} />
        {/* <Perf></Perf> */}
        <Bvh firstHitOnly enabled={enabled}>
          <Rays>
            <Torus />
          </Rays>
        </Bvh>
        <OrbitControls />
      </Canvas>
    </>
  );
}

export default React.memo(App);
